import type { CardDef } from '../../../../core/card';
import { cardDescription } from '../../../../core/cardText';
import { ACID, LIGHT } from '../../../palette';
import { plateCode } from '../codes';
import { clipHalfPlane, pt, type Pt } from '../geom';
import { box, fillPoly, label, line, solid, type FaceSpec, type Prim } from '../spec';
import { categoryOf } from './frame';
import { cjkTitle, latinTitle } from './util';

/**
 * **归还 Return —— `Catch` 捕获。**
 *
 * 操作是**归还**：楔从牌外上方插进一摞横条之间，埋进去的那一段是**虚线** ——
 * 那是全副牌共用的韵脚「虚线 = 这块不在你手上」。
 *
 * 插到第 3 层，因为它的效果是"塞回牌堆**任意位置**"：层数是**读出来的**，
 * 不是装饰。楔尖停在哪一层，玩家就知道能塞多深。
 *
 * ⚠️ 楔是薄荷色，**不是印刷红**。`PRESS.red` 是异常牌专属的那管墨，
 *    捕获把它降级了 —— 救命牌用的是这台机器自己的酸色。这不是配色偏好：
 *    红楔是"刺穿"，薄荷楔是"收回"，两件事。
 */

/** 牌堆顶部与层距（设计单位） */
const STACK_TOP = 40;
const LAYER_GAP = 11;
const LAYERS = 5;
/** 楔插进第 3 层（0 基）—— "任意位置" */
const INSERT_LAYER = 2;
/** 楔尖停在这一层的中线上 */
const TIP_Y = STACK_TOP + INSERT_LAYER * LAYER_GAP + 3.6;

/** 名牌块上的深色字。薄荷底上的黑要带一点绿，不然发脏 */
const DEEP = 0x03251a;

export function returnFace(card: CardDef): FaceSpec {
  const ink = ACID.mint;
  const tip = pt(50, TIP_Y);
  const wedge: Pt[] = [tip, pt(32, -14), pt(68, -14)];

  // ⚠️ 楔被牌堆分成两截，靠**几何裁剪**而不是 clipPath（IR 里没有裁剪路径）：
  //    上面那截是实心（还在空中），下面那截是虚线（已经塞进去了）
  const above = clipHalfPlane(wedge, 0, 1, STACK_TOP);
  const below = clipHalfPlane(wedge, 0, -1, -STACK_TOP);

  const art: Prim[] = [];
  // 牌堆：五条横条，一直伸到牌外
  for (let i = 0; i < LAYERS; i++) {
    art.push(box(-24, STACK_TOP + i * LAYER_GAP, 150, 6.6, solid(LIGHT.base, 0.16)));
  }
  // 埋进去的那段：虚线
  if (below.length >= 3) {
    art.push(
      line([...below, below[0]!], {
        width: 1.4,
        color: ink,
        alpha: 0.9,
        dash: [3.4, 2.8],
      }),
    );
  }
  // 还在空中的那段：实心
  if (above.length >= 3) art.push(fillPoly(above, solid(ink)));

  return {
    ground: solid(0x000000),
    accent: ink,
    prims: [
      label(plateCode(card.local), 6, 10.4, { size: 4.6, color: LIGHT.base, weight: 700, tracking: 1.2 }),
      label(categoryOf(card), 94, 10.4, {
        size: 4.1,
        color: ink,
        role: 'cjk',
        anchor: 'end',
        tracking: 0.9,
      }),
      // 名牌块只占左边 64 —— 右边留着露底下的楔，牌名和图形不抢地方
      box(0, 104, 64, 24, solid(ink)),
      label(cjkTitle(card), 4.5, 121, {
        size: 15,
        color: DEEP,
        role: 'cjk',
        weight: 900,
        tracking: 1.4,
        wrap: 5,
        lineHeight: 16,
      }),
      label(latinTitle(card), 4.5, 126, { size: 3.2, color: DEEP, tracking: 2.1 }),
      label(plateCode(card.local), 94, 126, { size: 3.1, color: DEEP, anchor: 'end', tracking: 1.6 }),
      box(0, 130, 100, 10, solid(0x000000, 0.85)),
      label(cardDescription(card), 4.5, 137.2, {
        size: 3.5,
        color: LIGHT.base,
        role: 'cjk',
        wrap: 26,
        lineHeight: 4.9,
      }),
    ],
    press: true,
    // 牌堆和楔都伸到牌外 —— 它们是"桌面上的东西"，不是印在牌上的字
    spill: art,
  };
}
